import { UserCircle } from '@assets/svgs';
import { FallbackHeader } from '@components/errorBoundary/FallbackHeader';
import { HeaderType, headerHeight } from '@components/header/Header.constants';
import { HeaderButtonProps, HeaderButtons } from '@components/header/HeaderButton';
import { HeaderContainer } from '@components/header/HeaderContainer';
import { HeaderLogo } from '@components/header/HeaderLogo';
import { QuoteDescriptionHeader } from '@components/header/QuoteDescriptionHeader';
import { ProfileBadge } from '@components/logout/ProfileBadge';
import { IMenuItem } from '@components/menu';
import { NotificationBadge } from '@components/notification/NotificationBadge';
import { QuoteGetResponse } from '@lio/api-types';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useStore } from '@store/GlobalStore';
import { arraysOverlap } from '@utils/arrays';
import debounce from 'lodash.debounce';
import { ReactNode, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { NavLink, useLocation } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { EnablePaymentPortal, useApiEnvStore } from '@utils/api-client';
import { DebugModeControls } from '@components/debugModeControls/debugModeControls';
import { activeLinkStyle, navBoxStyle, navLinkStyle } from './Header.styles';

export type navLink = {
  name: string;
  url: string;
  roles: string[];
  matchPartialRoute?: boolean;
  isVisible?: (on: boolean) => boolean;
  mobileSubLinks: {
    name: string;
    url: string;
    roles: string[];
  }[];
};

interface IHeader {
  type: HeaderType;
  quote?: QuoteGetResponse;
  navTitle?: string;
  buttons?: (HeaderButtonProps | React.ReactNode)[] | undefined;
  actionItems?: IMenuItem[];
  initialBgColor: any;
  scrolledBgColor: any;
  showLogoutBtn?: boolean;
  showNavLinks?: boolean;
  navLinks?: navLink[];
  disableActionItems?: boolean;
  hasUnsavedChanges?: boolean;
  smallLogo?: boolean;
}

interface IHeaderContainer {
  children: ReactNode;
  initialBgColor: string;
  scrolledBgColor: string;
  type: HeaderType;
  quote?: QuoteGetResponse;
}

const HeaderErrorBoundary = ({ type, scrolledBgColor, initialBgColor, children, quote }: IHeaderContainer) => {
  const [isScrolledDown, setIsScrolledDown] = useState(false); // 10/9/24  TODO this no longer works. Looks like we can use useScrollTrigger() hook from MUI
  const headerStyle = {
    position: 'static',
    height: `${headerHeight}px`,
    borderRadius: 0,
    background: isScrolledDown ? scrolledBgColor : initialBgColor,
    boxShadow: isScrolledDown && ![HeaderType.Detail].includes(type) ? 'boxShadow.4' : 'none',
  };

  const updateValue = debounce((val: boolean) => {
    setIsScrolledDown(val);
  }, 15);

  const changeNavbarColor = () => {
    if (window.scrollY >= 60) {
      updateValue(true);
    } else {
      updateValue(false);
    }
  };

  // NOTE 4/17: This is not actually working, changeNavbarColor is never called.
  useEffect(() => {
    if (!quote) {
      window.addEventListener('scroll', changeNavbarColor);
      return () => window.removeEventListener('scroll', changeNavbarColor);
    }
    return undefined;
    // eslint-disable-next-line
  }, []);

  return (
    <ErrorBoundary FallbackComponent={FallbackHeader}>
      <HeaderContainer headerStyle={headerStyle}>{children}</HeaderContainer>
    </ErrorBoundary>
  );
};

export const Header = ({
  type = HeaderType.Nav,
  quote,
  navLinks,
  showNavLinks = true,
  navTitle,
  buttons,
  actionItems,
  initialBgColor,
  scrolledBgColor,
  disableActionItems,
  hasUnsavedChanges,
  smallLogo,
}: IHeader) => {
  const roles = useStore((state) => state.roles, shallow);
  const [debugMode] = useApiEnvStore((state) => [state.debugMode], shallow);
  const location = useLocation();
  const theme = useTheme();
  const showSmallLogo = smallLogo || [HeaderType.Detail, HeaderType.Admin, HeaderType.PlaceHolder].includes(type);

  return (
    <HeaderErrorBoundary
      type={type}
      scrolledBgColor={scrolledBgColor}
      initialBgColor={initialBgColor}
      key='DefaultHeader'
    >
      <Grid
        container
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Grid sx={{ minWidth: 'fit-content', flex: 1 }}>
          <Grid
            container
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginTop: '0',

              '.MuiGrid-item': {
                paddingTop: showSmallLogo ? '0' : '24px',
              },
            }}
          >
            <HeaderLogo
              smallLogo={showSmallLogo}
              containerStyle={{ marginRight: showNavLinks ? '16px' : '', pr: 2, pt: showSmallLogo ? 0 : 3 }}
              iconStyle={{
                height: showSmallLogo ? '50px' : `${headerHeight}px`,
                width: showSmallLogo ? '50px' : `${headerHeight}px`,
              }}
              hasUnsavedChanges={hasUnsavedChanges}
            />
            {navTitle && (
              <Typography
                variant='h2'
                sx={{
                  textAlign: 'center',
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                {navTitle}
              </Typography>
            )}

            {navLinks?.map((link: navLink) =>
              arraysOverlap(link?.roles ?? [], roles) && (link.isVisible ? EnablePaymentPortal : true) ? (
                <Box sx={navBoxStyle} key={link.name}>
                  <NavLink
                    key='policies'
                    end
                    to={link.url}
                    style={({ isActive }) => {
                      let routeActive = false;
                      if (link.matchPartialRoute && location.pathname.includes(link.url)) {
                        routeActive = true;
                      }

                      return isActive || routeActive ? activeLinkStyle(theme) : navLinkStyle(theme);
                    }}
                  >
                    {link.name}
                  </NavLink>
                </Box>
              ) : null,
            )}
            {quote && <QuoteDescriptionHeader {...quote} />}
          </Grid>
        </Grid>
        <Grid sx={{ minWidth: 'fit-content' }}>
          <Grid
            container
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {debugMode && <DebugModeControls />}
            <NotificationBadge />
            <ProfileBadge
              iconButtonProps={{
                sx: { color: 'common.white' },
                children: <UserCircle width='20px' height='20px' />,
              }}
            />
            <HeaderButtons buttons={buttons} actionItems={actionItems} disableActionItems={disableActionItems} />
          </Grid>
        </Grid>
      </Grid>
    </HeaderErrorBoundary>
  );
};
